var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCallback } from 'react';
import { useResourceContext, useGetRecordId } from 'react-admin';
import { useGetLock } from '../dataProvider/useGetLock';
import { useSubscribeToLock } from './useSubscribeToLock';
/**
 * Fetch the lock for the current record, with live updates
 *
 * Calls `dataProvider.getLock()` for the current record on mount,
 * and subscribes to live updates on the `lock/[resource]/id` topic.
 * This means that if the lock is acquired or released by another user
 * while the current user is on the page, the return value will be updated.
 *
 * @example
 * import { useGetLockLive } from '@react-admin/ra-realtime';
 *
 * const LockStatus = () => {
 *   const { data: lock } = useGetLockLive();
 *   const { identity } = useGetIdentity();
 *   if (!lock) return <span>No lock</span>;
 *   if (lock.identity === identity?.id) return <span>Locked by you</span>;
 *   return <span>Locked by {lock.identity}</span>;
 * };
 *
 * @example // pass resource and record as options
 * import { useGetLockLive } from '@react-admin/ra-realtime';
 *
 * const LockStatus = ({ resource, record }) => {
 *   const { data: lock } = useGetLockLive(resource, { id: record?.id });
 *   const { identity } = useGetIdentity();
 *   if (!lock) return <span>No lock</span>;
 *   if (lock.identity === identity?.id) return <span>Locked by you</span>;
 *   return <span>Locked by {lock.identity}</span>;
 * };
 */
export var useGetLockLive = function (resourceParam, params, options) {
    var resource = useResourceContext({ resource: resourceParam });
    var id = useGetRecordId(params === null || params === void 0 ? void 0 : params.id);
    var meta = (params || {}).meta;
    var _a = options || {}, onEventReceived = _a.onEventReceived, queryOptions = __rest(_a, ["onEventReceived"]);
    var response = useGetLock(resource, { id: id, meta: meta }, __assign({ enabled: id !== undefined }, queryOptions));
    var refetch = response.refetch;
    var defaultCallback = useCallback(function () {
        refetch({ cancelRefetch: true });
    }, [refetch]);
    useSubscribeToLock(onEventReceived || defaultCallback, resource, id);
    return response;
};
